import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Users, PartyPopper } from "lucide-react";
import { IMAGES, EVENT_TYPES } from "@/data/content";
import { api } from "@/lib/api";

const initial = { name: "", phone: "", email: "", event_date: "", guest_count: "", event_type: EVENT_TYPES[0], special_requests: "" };

export default function Catering() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.catering({ ...form, guest_count: parseInt(form.guest_count || "0", 10) });
      toast.success("Request received! Our events team will reach out within 24 hours.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please call us at (248) 544-1810.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-transparent border-b-2 border-ink/15 focus:border-terracotta outline-none py-2.5 text-ink placeholder-ink/40 transition-colors duration-300";

  return (
    <section id="catering" className="bg-cream py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-16">
          <p className="text-terracotta text-xs uppercase tracking-[0.25em] mb-5">Catering & Events</p>
          <h2 className="font-serif-display text-5xl md:text-7xl text-ink leading-[0.9]">
            Bring the fire to <em className="text-terracotta not-italic">your table</em>.
          </h2>
          <p className="text-ink/65 mt-5 text-lg">Weddings, corporate lunches, private parties. Grazing boards to full-service. Tell us about your event and we'll send a tailored quote.</p>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="md:col-span-7 grid grid-cols-2 gap-6">
            <div className="col-span-2 overflow-hidden rounded-[2px] h-64 md:h-80">
              <img src={IMAGES.catingHero} alt="Catering charcuterie spread" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden rounded-[2px] h-44 md:h-56">
              <img src={IMAGES.cater1} alt="Event grazing board" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden rounded-[2px] h-44 md:h-56 relative bg-charcoal text-cream flex flex-col justify-center p-6">
              <div className="grid grid-cols-1 gap-3 text-sm">
                <span className="flex items-center gap-2"><PartyPopper size={16} className="text-terracotta" /> Weddings & receptions</span>
                <span className="flex items-center gap-2"><Users size={16} className="text-terracotta" /> Corporate lunches</span>
                <span className="flex items-center gap-2"><CalendarDays size={16} className="text-terracotta" /> Private parties</span>
              </div>
            </div>
          </motion.div>

          <motion.form onSubmit={submit} data-testid="catering-form"
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="md:col-span-5 bg-sand/60 border border-ink/10 rounded-lg p-7 md:p-9 flex flex-col gap-6">
            <h3 className="font-serif-display text-3xl text-ink">Request a Quote</h3>
            <input required data-testid="catering-name" placeholder="Full name" className={inputCls} value={form.name} onChange={set("name")} />
            <div className="grid grid-cols-2 gap-6">
              <input required data-testid="catering-phone" placeholder="Phone" className={inputCls} value={form.phone} onChange={set("phone")} />
              <input required type="email" data-testid="catering-email" placeholder="Email" className={inputCls} value={form.email} onChange={set("email")} />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <input required type="date" data-testid="catering-date" className={inputCls} value={form.event_date} onChange={set("event_date")} />
              <input required type="number" min="1" data-testid="catering-guests" placeholder="Guest count" className={inputCls} value={form.guest_count} onChange={set("guest_count")} />
            </div>
            <select data-testid="catering-type" className={inputCls} value={form.event_type} onChange={set("event_type")}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <textarea data-testid="catering-notes" rows={3} placeholder="Special requests" className={inputCls + " resize-none"} value={form.special_requests} onChange={set("special_requests")} />
            <button type="submit" disabled={loading} data-testid="catering-submit"
              className="bg-terracotta text-cream py-4 rounded-full text-sm hover:bg-ink transition-colors duration-300 active:scale-95 disabled:opacity-60">
              {loading ? "Sending..." : "Request My Quote"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
